// Vegetation sprite draw — the game's tree / shrub sprite materials in ONE
// module: the look hooks (vegLook / vegOwnLook; their GLSL chunks are
// defined by look.js setupLook), the atlases, the crossed quads, the sprite
// shader patches, the near-field hero extras and the instance transforms.
// trees.js, shrubs.js and grass.js build the game's vegetation from it; the
// dashboard's mini forest (vegforest.js, GET /api/vegforest.js) draws the
// owner's sprite through the very same code. three + vegpaint.js +
// renderconfig.json only: no game state, no fetches.
import * as THREE from 'three';
import RENDER_CFG from './renderconfig.json' with { type: 'json' };   // node-loadable (check-draw imports grass.js)
import {
  NVAR, BUSH_VAR, BUSH_ASPECT, paintSpeciesCanvas, paintBushCanvas, treeSpriteAtlas, vegSpriteLook,
} from './vegpaint.js';
import { SEASON, SNOW_GLSL, SPR_SEASON_GLSL } from './season.js';
import { act } from './sched.js';

// The owner's tree / shrub sprite as the bake ships it (pipeline/bake2.py
// `_ship_sprites`): veg/<sha16>.webp, named by the sha16 of the owner's PNG
// (meta.vegetation species[i].sprite, shrubs.sprite_leafy / sprite_needle).
export const vegSpriteUrl = (baseUrl, sha) => `${baseUrl}/veg/${sha}.webp`;

// The vegetation sprites' ONE colour hook (trees, shrubs, grass, bushes,
// flowers): their canvases are display-ready colours, not albedo x light, so
// before the scene curve they get renderconfig look.veg gain / saturation —
// the chunk look.js setupLook defines (APP_SURFACE.md "Light (texture rev 3)").
export function vegLook(shader) {
  shader.fragmentShader = shader.fragmentShader.replace('#include <tonemapping_fragment>',
    '#include <veg_look_fragment>\n#include <tonemapping_fragment>');
}

// …and its owner-PNG form (renderconfig look.veg_sprite, per world
// region.vegetation.sprite_look): a material that may hold the owner's
// picture (tree species atlases, the shrub layer, the near-field bushes)
// takes vegOwnLook instead. Painted and owner columns share ONE program: the
// look is chosen per fragment from uniforms (chunk veg_own_look_fragment,
// look.js setupLook), so a PNG landing flips a uniform, never a recompile.
// VEG_SPRITE_LOOK = (gain, sat) for every owner material of the world;
// own = the material's {value: (col 0, col >= 1)} flags, 1 = that atlas
// column holds the owner's PNG (a tree PNG fills every column: both 1).
// The vertex stage needs `attribute float aVar` declared (every user has it).
const _vsl = RENDER_CFG.look.veg_sprite;
export const VEG_SPRITE_LOOK = { value: new THREE.Vector2(_vsl.gain, _vsl.sat) };
export function setVegSpriteLook(veg) {
  const l = vegSpriteLook(veg && veg.sprite_look, _vsl);
  VEG_SPRITE_LOOK.value.set(l.gain, l.sat);
}
export const vegOwnState = () => ({ value: new THREE.Vector2(0, 0) });
export function vegOwnLook(shader, own) {
  shader.uniforms.uVegLook = VEG_SPRITE_LOOK;
  shader.uniforms.uVegOwn = own;
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', '#include <common>\nuniform vec2 uVegOwn;\nvarying float vVegOwn;')
    .replace('#include <begin_vertex>', '#include <begin_vertex>\nvVegOwn = aVar < 0.5 ? uVegOwn.x : uVegOwn.y;');
  shader.fragmentShader = shader.fragmentShader
    .replace('#include <common>', '#include <common>\nuniform vec2 uVegLook;\nvarying float vVegOwn;')
    .replace('#include <tonemapping_fragment>',
      '#include <veg_own_look_fragment>\n#include <tonemapping_fragment>');
}

// the dashboard's canvas atlases (vegforest.js): makeBushAtlas /
// makeSpeciesAtlas / spriteAtlas — a CanvasTexture over a document canvas
// the painters fill. The game paints the same atlases in the pool
// (poolAtlas below): the same painters, the same pixels.
export function makeBushAtlas(colorHex, bloomHex, S = 64) {
  const cv = document.createElement('canvas');
  paintBushCanvas(cv, colorHex, bloomHex, S);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  return tex;
}

// ------------------------------------------------------------- sprite shader
// One patch for every species material: per-instance atlas column (aVar),
// tip-weighted wind sway phased by the instance's world position, and a
// derivative-sharpened alpha test — mipmap averaging otherwise erodes fine
// needle strokes to nothing at distance (the classic foliage fade-out).
// The season (gs-sprite-snow, APP_FORESTVOL §5): the atlas's season page
// (seasonPage: R snow-catch, G foliage) + the species' tint / bare / catch
// (crowns.js spriteSeason → setSpriteSeason) + THE world uSeason (season.js
// SEASON) through season.js sprSeason, after the map. Without a page or a
// season set (the dashboard's mini forest) it draws as painted.
const _noPage = (() => {
  const t = new THREE.DataTexture(new Uint8Array([0, 255, 0, 255]), 1, 1);
  t.needsUpdate = true;
  return t;
})();
// the season plumbing of a sprite material (trees: patchSpriteMat; the
// shrubs: patchShrubMat {season}): the page + per-material uniforms in
// mat.userData.sprSeason, THE uSeason, the fragment's world y and catch
// (catchVS: the VS expression of the catch — a uniform, or per atlas column)
function seasonState() {
  return {
    uSprPage: { value: _noPage }, uSprPageSize: { value: new THREE.Vector2(1, 1) },
    uSprSeason: { value: new THREE.Vector4(1, 1, 1, 0) }, uSprCatch: { value: new THREE.Vector2(0, 0) },
  };
}
function seasonPatch(shader, ss, catchVS) {
  Object.assign(shader.uniforms, ss);
  shader.uniforms.uSeason = SEASON.uSeason;
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', '#include <common>\nuniform vec2 uSprCatch;\nvarying float vSprY, vSprCatch;')
    .replace('#include <project_vertex>', `#include <project_vertex>
        {
          vec4 sprW = vec4(transformed, 1.0);
        #ifdef USE_INSTANCING
          sprW = instanceMatrix * sprW;
        #endif
          vSprY = (modelMatrix * sprW).y;
          vSprCatch = ${catchVS};
        }`);
  shader.fragmentShader = shader.fragmentShader
    .replace('#include <common>', `#include <common>
uniform sampler2D uSprPage;
uniform vec2 uSprPageSize;
uniform vec4 uSprSeason;
uniform vec4 uSeason[2];
varying float vSprY, vSprCatch;
${SNOW_GLSL}${SPR_SEASON_GLSL}
#ifndef SPR_SNOW_K
#define SPR_SNOW_K 1.0
#endif`)
    .replace('#include <map_fragment>', `#include <map_fragment>
  diffuseColor.rgb = sprSeason(diffuseColor.rgb, vMapUv, uSprPageSize, texture2D(uSprPage, vMapUv),
    uSprSeason, vSprCatch, vSprY, SPR_SNOW_K);`);
}

// ALPHA-TO-COVERAGE (vr-shimmer, 2026-11-02; APP_SURFACE_PERF "vr-shimmer"). The sprites' derivative-sharpened
// alpha (a ramp ONE pixel wide about the alphaTest iso-line) was resolved by a binary discard at 0.5: under
// MSAA every foliage edge stayed one-sample aliased and, once a sprite is minified, the mip's averaged alpha
// (fine needles: below alphaTest) was cut away — "half-far trees look like a set of pixels with gaps", edges
// that crawl under the head's sub-pixel motion (owner, Quest 3). The standard completion: alphaToCoverage —
// the ramp becomes the fragment's sample coverage (an anti-aliased edge at 4x MSAA), and from mip ~1 on the
// coverage blends to the mip's own averaged alpha (alpha / (2 alphaTest): the area the level-0 test keeps),
// so a minified crown keeps its true covered area instead of clumps and holes. Where the pass's target has
// ONE sample (A2C is then a GL no-op: the VR scope's lens RT, the optic's 8-bit target) uVegMs = 0 and the
// shader is the old sharpen + discard at 0.5 + an opaque alpha, the same pixels. On a multisampled target the
// fragment's alpha is the coverage only: the draw masks the alpha channel (vegCoverage), so no fractional
// alpha reaches the canvas / the XR layer.
export const VEG_A2C_CUT = 0.02;      // below this the fragment covers no sample of 4: skip its depth + fill
export function vegAlphaGLSL() {
  return `#ifdef USE_ALPHATEST
        float aw = max(fwidth(diffuseColor.a), 1e-4);
        #ifdef ALPHA_TO_COVERAGE
          float aCov = clamp((diffuseColor.a - alphaTest) / aw + 0.5, 0.0, 1.0);
          #ifdef USE_MAP
            vec2 aTx = vMapUv * vec2(textureSize(map, 0));
            float aLod = 0.5 * log2(max(max(dot(dFdx(aTx), dFdx(aTx)), dot(dFdy(aTx), dFdy(aTx))), 1e-8));
            aCov = mix(aCov, clamp(diffuseColor.a * (0.5 / alphaTest), 0.0, 1.0), uVegMs * smoothstep(1.0, 3.0, aLod));
          #endif
          if (aCov < mix(0.5, ${VEG_A2C_CUT.toFixed(2)}, uVegMs)) discard;
          diffuseColor.a = mix(1.0, aCov, uVegMs);   // one sample: opaque, as before
        #else
          diffuseColor.a = clamp((diffuseColor.a - alphaTest) / aw + 0.5, 0.0, 1.0);
          if (diffuseColor.a < 0.5) discard;
        #endif
      #endif`;
}
// the pass's target multisampled? (three's XR target: its samples; the multiview one (xrmv.js) and the
// canvas: the context's antialias)
const _ctxAA = new WeakMap();
function targetMs(renderer) {
  const rt = renderer.getRenderTarget();
  if (rt && !rt.multiview) return rt.samples > 0;
  if (rt && rt.multiview && typeof rt.mvSamples === 'number') return rt.mvSamples > 0;   // xrmv.js: the Quest's runtime MSAA (w35)
  let aa = _ctxAA.get(renderer);
  if (aa === undefined) { const at = renderer.getContextAttributes(); aa = !!(at && at.antialias); _ctxAA.set(renderer, aa); }
  return aa;
}
function vegA2cAfter(renderer) { renderer.getContext().colorMask(true, true, true, true); }
// a sprite material made an alpha-to-coverage one (its onBeforeCompile declares uVegMs: vegCoverageDecl).
// Per draw (Material.onBeforeRender, after the pass's target is bound): uVegMs from the target; on a
// multisampled one the colour writes skip alpha for this draw (the object's onAfterRender restores them —
// three's own colour-mask cache stays true throughout, so it never sees the mask)
export function vegCoverage(mat) {
  mat.alphaToCoverage = true;
  const ms = mat.userData.vegMs = { value: 1 };
  mat.onBeforeRender = (renderer, _scene, _camera, _geometry, object) => {
    const on = targetMs(renderer) ? 1 : 0;
    if (ms.value !== on) ms.value = on;
    if (!on) return;
    renderer.getContext().colorMask(true, true, true, false);
    if (object.onAfterRender !== vegA2cAfter && !object.userData.vegA2c) {
      const prev = object.onAfterRender;
      object.userData.vegA2c = true;
      object.onAfterRender = prev === THREE.Object3D.prototype.onAfterRender ? vegA2cAfter
        : function (...a) { vegA2cAfter(a[0]); return prev.apply(this, a); };
    }
  };
  return mat;
}
export function vegCoverageDecl(shader, mat) {
  shader.uniforms.uVegMs = mat.userData.vegMs;
  shader.fragmentShader = shader.fragmentShader.replace('#include <common>', '#include <common>\nuniform float uVegMs;');
}

export function patchSpriteMat(mat, time) {
  const ss = mat.userData.sprSeason = seasonState();
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = time;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>',
        '#include <common>\nuniform float uTime;\nattribute float aVar;')
      .replace('#include <uv_vertex>',
        `#include <uv_vertex>
        vMapUv.x = (vMapUv.x + aVar) / ${NVAR}.0;`)
      .replace('#include <begin_vertex>',
        `#include <begin_vertex>
        {
          float ph = instanceMatrix[3][0] * 0.37 + instanceMatrix[3][2] * 0.23;
          float sw = position.y * position.y * 0.025;
          transformed.x += sin(uTime * 1.05 + ph) * sw;
          transformed.z += cos(uTime * 0.83 + ph * 1.7) * sw;
        }`);
    seasonPatch(shader, ss, 'uSprCatch.x');
    vegCoverageDecl(shader, mat);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <alphatest_fragment>', vegAlphaGLSL());
    vegOwnLook(shader, mat.userData.vegOwn);   // painted, or the owner's PNG once it lands
  };
  mat.userData.vegOwn = vegOwnState();
  vegCoverage(mat);
  mat.customProgramCacheKey = () => 'treesprite3:' + NVAR;
}

// ------------------------------------------- atlases painted in the pool
// R8c3: the game's atlases (trees.js species, shrubs.js / grass.js bushes,
// grass.js tufts + flowers) are painted in the client worker pool on an
// OffscreenCanvas (pool-veg.js veg.paint: the vegpaint.js painters), the
// owner's sprite composited there (veg.sprite) and the season page read
// there (veg.page) — the canvas lives in ONE worker (affinity 'veg': every
// job of an atlas meets its canvas, in the order asked) and only bytes come
// back; the main thread never reads a canvas back (the readback behind the
// game's GPU queue was R8's 927 ms 'veg.page.read'). run = pool.js run, handed
// in by the game's modules: vegmat stays pool-free, so the dashboard's mini
// forest (vegforest.js) keeps its canvas atlases (makeSpeciesAtlas /
// makeBushAtlas / spriteAtlas above and below — the same painters).
//
// An atlas handle: {id, kind, args, sprites, tex, lum, ready}. tex exists at
// once (a material is made with it and compiles its program) but holds no
// pixels until `ready` resolves: the caller draws its material only then
// (material.visible) — nothing draws a missing or placeholder atlas. Its
// kept inputs (kind + args + the owner's sprite blobs) paint it again when
// the worker was lost (the pool replaces a dead worker EMPTY: a job then
// meets no canvas, or the job died with the worker) — once per job.
const VEG_JOB = { affinity: 'veg', prio: 'near' };   // ONE prio: the worker runs an atlas's jobs FIFO
const LOST = /veg: no atlas |pool: worker \d+ gone/;
const lost = (e) => LOST.test((e && e.message) || '');
let _aid = 0;
const _drop = [];               // disposed atlases' ids: their worker canvases go with the next veg job

// the texture the bytes (getImageData order: top row first, straight alpha)
// go in: a main-thread canvas they are PUT into (putImageData — no paint, no
// readback), uploaded as today's CanvasTexture uploads its canvas (Texture
// over a canvas: flipY, no premultiply, the colour space given, trilinear,
// mipmaps). Not a DataTexture of the bytes: WebGL un-premultiplies the
// canvas on upload otherwise than getImageData does — up to 1.8/255 off on
// semi-transparent texels (check-vegatlas.mjs --browser, R8c3); the canvas
// the bytes are put back into uploads EXACTLY as the painted one did (the
// pages: today's swapPage did the same). A plain Texture: version 0 until
// its bytes land — never uploaded before (a CanvasTexture starts at 1).
export function atlasTexture(colorSpace = THREE.SRGBColorSpace) {
  const t = new THREE.Texture(document.createElement('canvas'));
  t.colorSpace = colorSpace;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.generateMipmaps = true;
  return t;
}
export function setAtlasBytes(t, px, w, h) {
  const cv = t.image;
  if (cv.width !== w || cv.height !== h) { cv.width = w; cv.height = h; }   // the first landing only (an atlas keeps its size)
  cv.getContext('2d').putImageData(new ImageData(new Uint8ClampedArray(px.buffer, px.byteOffset, px.byteLength), w, h), 0, 0);
  t.needsUpdate = true;
}

function vegJob(a, op, args, transfer = []) {
  if (a.dead) return Promise.reject(new Error(`veg atlas ${a.id} disposed`));
  return a.run(op, { id: a.id, drop: _drop.splice(0), ...args }, { ...VEG_JOB, transfer });
}
// (re)paint a's canvas from its kept inputs, every kept sprite composited again
async function paint(a) {
  const job = async () => {
    const bms = await Promise.all(a.sprites.map((s) => createImageBitmap(s.blob)));
    return vegJob(a, 'veg.paint', { kind: a.kind, args: a.args, sprites: a.sprites.map((s, k) => ({ cell: s.cell, bm: bms[k] })) }, bms);
  };
  try { return await job(); } catch (e) { if (!lost(e)) throw e; return job(); }
}
function land(a, r) {
  if (a.dead) return;
  setAtlasBytes(a.tex, r.px, r.w, r.h);
  if (r.lum) a.lum = r.lum;
}

// a new atlas painted in the pool: kind species {form, color, S, forms} |
// bush {color, bloom, S} | tuft {S, nvar} | flower {}
export function poolAtlas(run, kind, args = {}, colorSpace = THREE.SRGBColorSpace) {
  const a = { run, id: `${kind}${++_aid}`, kind, args, sprites: [], tex: atlasTexture(colorSpace), lum: null, dead: false };
  a.ready = paint(a).then((r) => { land(a, r); return a; });
  return a;
}

// the owner's sprite (a Blob: the shipped WebP) into a's canvas — cell
// 'tree': every species cell; v: bush column v — then a's texture re-uploads.
// Resolves true once the bytes landed; false for a disposed atlas.
export async function atlasSprite(a, blob, cell) {
  const bm = await createImageBitmap(blob);
  if (a.dead) { bm.close(); return false; }
  a.sprites = a.sprites.filter((s) => s.cell !== cell).concat({ cell, blob });   // kept: a repaint composites it again
  let r;
  try { r = await vegJob(a, 'veg.sprite', { cell, bm }, [bm]); } catch (e) {
    if (!lost(e)) throw e;
    r = await paint(a);              // the canvas was lost: painted again, this sprite included
  }
  land(a, r);
  return !a.dead;
}

// a's canvas goes (its texture now, its worker canvas with the next veg job)
export function disposeAtlas(a) {
  if (!a || a.dead) return;
  a.dead = true;
  _drop.push(a.id);
  a.tex.dispose();
}

// the atlas's season page (the painted forms and the owner's PNG alike —
// derived on device, vegpaint.js seasonPage) read and derived in the pool
// (veg.page: after every job asked before it on that atlas), into the
// material's page uniform; the old page is disposed. A newer call for the
// same material supersedes: the latest wins, an older result is dropped and
// its promise resolves with the newer one's (its pixels are in it: the atlas
// is painted cumulatively) -> resolves once a page holding the call's atlas
// state is bound, so a caller flips its owner look after it, never before.
// The swap carries its long-task label ('veg.page.swap').
export function setSpritePage(mat, a, alpha) {
  const ud = mat.userData, gen = ud.sprPageGen = (ud.sprPageGen || 0) + 1;
  return (ud.sprPageP = pageOf(a, Math.round(alpha * 255))
    .then((r) => (gen !== ud.sprPageGen ? ud.sprPageP : act('veg.page.swap', () => swapPage(mat, r.page, r.w, r.h)))));
}
async function pageOf(a, a0) {
  try { return await vegJob(a, 'veg.page', { a0 }); } catch (e) {
    if (!lost(e)) throw e;
    land(a, await paint(a));         // the canvas was lost: painted again from the kept inputs
    return vegJob(a, 'veg.page', { a0 });
  }
}
function swapPage(mat, page, w, h) {
  const tex = atlasTexture(THREE.NoColorSpace);   // today's page texture: the atlas's orientation (flipY) and filtering
  setAtlasBytes(tex, page, w, h);
  const ss = mat.userData.sprSeason;
  if (ss.uSprPage.value !== _noPage) ss.uSprPage.value.dispose();
  ss.uSprPage.value = tex;
  ss.uSprPageSize.value.set(w, h);
  return true;
}
// the species' season (crowns.js spriteSeason: {tint, bare, catch})
export function setSpriteSeason(mat, v) {
  const ss = mat.userData.sprSeason;
  ss.uSprSeason.value.set(v.tint[0], v.tint[1], v.tint[2], v.bare);
  ss.uSprCatch.value.set(v.catch, v.catch);
}
// the shrubs' season (patchShrubMat {season}): snow only — no tint, no bare
// (alpine rose, juniper, dwarf pine keep their leaves; gs-family-shrub) —
// catch per atlas column: leafy (column 0) the broadleaf's, needle (1) the
// conifer's (crowns.js SNOW_CATCH, passed in: vegmat stays crowns-free)
export function setShrubSeason(mat, leafyCatch, needleCatch) {
  const ss = mat.userData.sprSeason;
  ss.uSprSeason.value.set(1, 1, 1, 0);
  ss.uSprCatch.value.set(leafyCatch, needleCatch);
}

// ------------------------------------------------------------ sprite geometry
// two crossed vertical quads, pivot at the base, 1x1 unit (scaled per tree).
// Arrays are built once; each chunk wraps them in fresh BufferAttributes so
// its own aVar instance attribute can be disposed with the chunk.
let _geoParts = null;
export function makeTreeGeoParts() {
  if (_geoParts) return _geoParts;
  const pos = [], uv = [], idx = [];
  let vi = 0;
  for (const rot of [0, Math.PI / 2]) {
    const dx = Math.cos(rot) * 0.5, dz = Math.sin(rot) * 0.5;
    pos.push(-dx, 0, -dz, dx, 0, dz, dx, 1, dz, -dx, 1, -dz);
    uv.push(0, 0, 1, 0, 1, 1, 0, 1);
    idx.push(vi, vi + 1, vi + 2, vi, vi + 2, vi + 3);
    vi += 4;
  }
  _geoParts = { pos: new Float32Array(pos), uv: new Float32Array(uv), idx };
  return _geoParts;
}

export function buildSpriteGeometry(parts) {
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(parts.pos, 3));
  geo.setAttribute('uv', new THREE.BufferAttribute(parts.uv, 2));
  geo.setIndex(parts.idx.slice());
  return geo;
}

// --------------------------------------------------------------- sprite atlas
// NVAR silhouette variants per species, side by side in one texture — painted
// by vegpaint.js (the SAME painters the dashboard's Vegetation preview runs);
// this side only wraps the canvas in a texture.
export function makeSpeciesAtlas(form, colorHex, S, vegForms) {
  const cv = document.createElement('canvas');
  paintSpeciesCanvas(cv, form, colorHex, S, vegForms);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  return tex;
}

// the owner's sprite in all NVAR cells of a canvas atlas (the dashboard's
// mini forest): vegpaint.js treeSpriteAtlas, the composite the pool's
// veg.sprite runs on its worker canvas
export const spriteAtlas = treeSpriteAtlas;

// ---------------------------------------------------------------- hero trees
// Near-field extras (rev 81): a unit-height low-poly trunk (flat-shaded via
// baked per-vertex lambert, sun az 155 / alt 52 — matches the terrain light
// bake, MeshBasic only) plus a 2-quad sprite fan at 45°/135° that interleaves
// the chunk sprite's 0°/90° pair into an 8-plane star.
// Tri budget (validate.check_budgets): trunk 12 + fan 4 = 16/tree.

const _SUN = new THREE.Vector3(0.26, 0.79, 0.56).normalize();

function _shade(nx, ny, nz) {
  const l = Math.hypot(nx, ny, nz) || 1;
  const d = Math.max(0, (nx * _SUN.x + ny * _SUN.y + nz * _SUN.z) / l);
  return 0.62 + 0.55 * d;
}

export function makeHeroTrunkGeometry() {
  const pos = [], col = [], idx = [];
  const SEG = 6, r0 = 0.035, r1 = 0.022, y1 = 0.36;
  for (let s = 0; s <= SEG; s++) {
    const a = (s / SEG) * Math.PI * 2;
    const ca = Math.cos(a), sa = Math.sin(a);
    const k = _shade(ca, 0, sa);
    pos.push(ca * r0, 0, sa * r0, ca * r1, y1, sa * r1);
    col.push(k, k, k, k, k, k);
  }
  for (let s = 0; s < SEG; s++) {
    const v = s * 2;
    idx.push(v, v + 1, v + 2, v + 1, v + 3, v + 2);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  geo.setIndex(idx);
  return geo;
}

// the extra crossed pair for near trees. Same 1x1 unit layout as
// makeTreeGeoParts but rotated 45°, with a per-instance aVar attribute so the
// shared species material picks the SAME atlas column as the chunk sprite.
export function makeHeroFanGeometry(cap) {
  const pos = [], uv = [], idx = [];
  let vi = 0;
  for (const rot of [Math.PI / 4, (3 * Math.PI) / 4]) {
    const dx = Math.cos(rot) * 0.5, dz = Math.sin(rot) * 0.5;
    pos.push(-dx, 0, -dz, dx, 0, dz, dx, 1, dz, -dx, 1, -dz);
    uv.push(0, 0, 1, 0, 1, 1, 0, 1);
    idx.push(vi, vi + 1, vi + 2, vi, vi + 2, vi + 3);
    vi += 4;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  geo.setIndex(idx);
  geo.setAttribute('aVar', new THREE.InstancedBufferAttribute(new Float32Array(cap), 1));
  return geo;
}

// the hero trunk's material: pre-lit like the sprite trunks it continues
export function heroTrunkMaterial() {
  const m = new THREE.MeshBasicMaterial({ color: 0x4a3826, vertexColors: true, fog: true });
  m.onBeforeCompile = vegLook;
  return m;
}

// ------------------------------------------------------- instance transforms
// tree i at base (x, y, z), drawn height s (m), width factor wf: yaw from the
// index, base sunk 0.3 m so billboards never float on slopes. The chunk
// sprite and its hero fan + trunk share it (the fan interleaves at 45°).
const _up = new THREE.Vector3(0, 1, 0);
const _Q = new THREE.Quaternion();
const _S = new THREE.Vector3();
const _P = new THREE.Vector3();
export function treeMatrix(M, x, y, z, i, s, wf) {
  _Q.setFromAxisAngle(_up, (i % 31) / 31 * Math.PI);
  _S.set(s * wf, s, s * wf);
  _P.set(x, y - 0.3, z);
  return M.compose(_P, _Q, _S);
}
// tree i's silhouette variant (atlas column)
export const treeVar = (i) => (i * 1274126177 >>> 7) % NVAR;

// shrub i at base (x, y, z), height h (m): bushes wider than tall
export function shrubMatrix(M, x, y, z, i, h) {
  _Q.setFromAxisAngle(_up, (i % 29) / 29 * Math.PI);
  _S.set(h * BUSH_ASPECT, h, h * BUSH_ASPECT);
  _P.set(x, y - 0.08, z);
  return M.compose(_P, _Q, _S);
}

// ------------------------------------------------------------ shrub sprites
export function makeCrossedQuads() {
  const geo = new THREE.BufferGeometry();
  const pos = [], uv = [], idx = [];
  let vi = 0;
  for (const rot of [0, Math.PI / 2]) {
    const dx = Math.cos(rot) * 0.5, dz = Math.sin(rot) * 0.5;
    pos.push(-dx, 0, -dz, dx, 0, dz, dx, 1, dz, -dx, 1, -dz);
    uv.push(0, 0, 1, 0, 1, 1, 0, 1);
    idx.push(vi, vi + 1, vi + 2, vi, vi + 2, vi + 3);
    vi += 4;
  }
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  geo.setIndex(idx);
  return geo;
}

// atlas column select + gentle wind + derivative-sharpened alpha (the same
// treatment every scatter sprite gets — mipmaps otherwise erode the foliage);
// the look per column: painted, or the owner's PNG (vegOwnLook)
function shrubPatch(shader, mat) {
  vegCoverageDecl(shader, mat);
  shader.fragmentShader = shader.fragmentShader
    .replace('#include <alphatest_fragment>', vegAlphaGLSL());
  vegOwnLook(shader, mat.userData.vegOwn);
}

// mat's wind clock: time = {value: seconds} (Shrubs.time); season: the
// season page + snow (the game's Shrubs; setSpritePage / setShrubSeason) —
// without it (the dashboard's mini forest) the bushes draw as painted
export function patchShrubMat(mat, time, { season = false } = {}) {
  const ss = season ? (mat.userData.sprSeason = seasonState()) : null;
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = time;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>',
        '#include <common>\nuniform float uTime;\nattribute float aVar;')
      .replace('#include <uv_vertex>',
        `#include <uv_vertex>
        vMapUv.x = (vMapUv.x + aVar) / ${BUSH_VAR}.0;`)
      .replace('#include <begin_vertex>',
        `#include <begin_vertex>
        {
          float ph = instanceMatrix[3][0] * 0.41 + instanceMatrix[3][2] * 0.27;
          float sw = position.y * position.y * 0.05;
          transformed.x += sin(uTime * 1.2 + ph) * sw;
          transformed.z += cos(uTime * 0.9 + ph * 1.6) * sw;
        }`);
    if (ss) seasonPatch(shader, ss, 'aVar < 0.5 ? uSprCatch.x : uSprCatch.y');
    shrubPatch(shader, mat);
  };
  mat.userData.vegOwn = vegOwnState();
  vegCoverage(mat);
  mat.customProgramCacheKey = () => 'shrubwind2:' + BUSH_VAR + (ss ? ':season' : '');
}
